/**
 * Theme Video Component v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Web component for Shopify-hosted videos with lazy loading and playback control.
 *
 * Settings (via data attributes):
 * - data-autoplay: Auto-start playback (default: true)
 * - data-muted: Mute audio (default: true, required for autoplay)
 * - data-loop: Loop playback (default: true)
 * - data-controls: Show video controls (default: false)
 * - data-loading: eager | lazy | preload
 * - data-preload-margin: Pixels to preload before visible (default: 200)
 * - data-playback: auto | hover | click
 *
 * Usage:
 * ```html
 * <theme-video data-autoplay="true" data-muted="true" data-loop="true" data-loading="lazy">
 *   <video ref="video">
 *     <source data-src="video.mp4" type="video/mp4">
 *   </video>
 * </theme-video>
 * ```
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { Component } from './component-base.js';

/**
 * Theme Video Web Component
 * @extends Component
 */
export class ThemeVideo extends Component {
  /** @type {string[]} */
  requiredRefs = ['video'];

  // State
  #loaded = false;
  #playing = false;
  #observer = null;
  #visibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Read configuration from attributes
    this.autoplay = this.dataset.autoplay !== 'false';
    this.muted = this.dataset.muted !== 'false';
    this.loop = this.dataset.loop !== 'false';
    this.controls = this.dataset.controls === 'true';
    this.loading = this.dataset.loading || 'lazy';
    this.playback = this.dataset.playback || 'auto';
    this.preloadMargin = this.dataset.preloadMargin || '200';

    // Apply video element attributes
    const video = this.refs.video;
    video.muted = this.muted;
    video.loop = this.loop;
    video.playsInline = true;
    if (this.controls) {
      video.controls = true;
    } else {
      video.removeAttribute('controls');
    }

    // Setup loading strategy
    if (this.loading === 'eager') {
      this.#loadVideo();
    } else {
      this.#observeForLoading();
    }

    // Setup playback triggers
    if (this.playback === 'hover') this.#setupHoverPlay();
    if (this.playback === 'click') this.#setupClickPlay();

    // For auto playback with autoplay enabled, observe visibility
    if (this.playback === 'auto' && this.autoplay) {
      this.#observeVisibility();
    }

    this.log.debug('Connected', {
      autoplay: this.autoplay,
      muted: this.muted,
      loop: this.loop,
      controls: this.controls,
      loading: this.loading,
      playback: this.playback
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  // ─── Loading ────────────────────────────────────────────────────────────────

  /**
   * Observe viewport for lazy/preload loading
   */
  #observeForLoading() {
    const margin = this.loading === 'preload' ? `${this.preloadMargin}px` : '0px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.#loadVideo();
          this.#observer.disconnect();
          this.#observer = null;
        }
      },
      { rootMargin: margin }
    );

    this.#observer.observe(this);
    this.log.debug('Observing for load', { margin });
  }

  /**
   * Inject src attributes and load video
   */
  #loadVideo() {
    if (this.#loaded) return;

    const video = this.refs.video;
    const sources = this.querySelectorAll('source[data-src]');

    // Inject src from data-src
    sources.forEach((source) => {
      const src = source.dataset.src;
      if (src) {
        source.src = src;
        this.log.debug('Injected source', { src });
      }
    });

    // Trigger load
    video.load();

    // Listen for ready state
    video.addEventListener('canplay', () => {
      this.#loaded = true;
      this.setAttribute('data-loaded', '');
      this.log.debug('Video ready');

      // Auto-play if configured and visible
      if (this.playback === 'auto' && this.autoplay && this.#isVisible()) {
        this.play();
      }
    }, { once: true, ...this.signal });

    // Handle errors
    video.addEventListener('error', (e) => {
      this.log.error('Video error', { error: e.message || 'Unknown error' });
    }, { once: true, ...this.signal });
  }

  // ─── Visibility ─────────────────────────────────────────────────────────────

  /**
   * Observe visibility for auto-pause when off-screen
   */
  #observeVisibility() {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;

        if (isVisible && this.#loaded && this.playback === 'auto' && this.autoplay) {
          this.play();
        } else if (!isVisible && this.#playing) {
          this.pause();
        }
      },
      { threshold: 0.1 }
    );

    this.#visibilityObserver.observe(this);
  }

  /**
   * Check if element is currently visible
   */
  #isVisible() {
    const rect = this.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0
    );
  }

  // ─── Playback Triggers ──────────────────────────────────────────────────────

  /**
   * Setup hover-to-play behavior
   */
  #setupHoverPlay() {
    this.addEventListener('mouseenter', () => {
      if (this.#loaded) this.play();
    }, this.signal);

    this.addEventListener('mouseleave', () => {
      this.pause();
    }, this.signal);
  }

  /**
   * Setup click-to-play behavior
   */
  #setupClickPlay() {
    // Click on poster or play button
    const clickTarget = this.refs.poster || this.refs.playBtn || this;

    clickTarget.addEventListener('click', (e) => {
      e.preventDefault();

      if (!this.#loaded) {
        // Load first, then play
        this.#loadVideo();
        this.refs.video.addEventListener('canplay', () => this.play(), { once: true });
      } else {
        this.toggle();
      }
    }, this.signal);

    // Click on video to toggle
    if (this.controls) {
      // Let native controls handle it
    } else {
      this.refs.video.addEventListener('click', () => {
        this.toggle();
      }, this.signal);
    }
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Play the video
   */
  play() {
    if (this.#playing) return;

    const playPromise = this.refs.video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.#playing = true;
          this.setAttribute('data-playing', '');
          this.log.debug('Playing');
        })
        .catch((err) => {
          // Autoplay was prevented (usually needs user interaction first)
          this.log.warn('Play prevented', { error: err.message });
        });
    }
  }

  /**
   * Pause the video
   */
  pause() {
    if (!this.#playing) return;

    this.refs.video.pause();
    this.#playing = false;
    this.removeAttribute('data-playing');
    this.log.debug('Paused');
  }

  /**
   * Toggle play/pause
   */
  toggle() {
    if (this.#playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Check if video is loaded
   */
  get loaded() {
    return this.#loaded;
  }

  /**
   * Check if video is playing
   */
  get playing() {
    return this.#playing;
  }
}

// Register component
if (!customElements.get('theme-video')) {
  customElements.define('theme-video', ThemeVideo);
  window.Theme?.Logger?.ns('theme-video').info('Registered');
}
